import { useEffect, useRef, useState as useStateRaw, type ReactNode } from 'react';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import { useLanguageContext } from '@/i18n/LanguageContext';
import Seo from '@/components/seo/Seo';

const BUTLER_BELL_URL = 'https://butlerbell.jp/';

function FadeInSection({ children, className = '' }: { children: ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useStateRaw(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className={`fade-in-section ${isVisible ? 'is-visible' : ''} ${className}`}>
      {children}
    </div>
  );
}

export default function PresidentPage() {
  const { t } = useTranslation();
  const { lang } = useLanguageContext();
  const href = (path: string) => (lang === 'en' ? (path === '/' ? '/en' : `/en${path}`) : path);

  const paragraphs = [1, 2, 3, 5, 6, 4];

  const breadcrumbStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: t('president.hero.home'), item: 'https://butler-academy.jp/' },
      { '@type': 'ListItem', position: 2, name: t('president.hero.parent'), item: 'https://butler-academy.jp/about' },
      { '@type': 'ListItem', position: 3, name: t('president.hero.current'), item: 'https://butler-academy.jp/president' },
    ],
  };

  const webPageStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: t('president.hero.title'),
    description: t('president.hero.lead'),
    url: 'https://butler-academy.jp/president',
    isPartOf: {
      '@type': 'WebSite',
      name: lang === 'en' ? 'Japan Butler Academy' : '日本執事アカデミー',
      url: 'https://butler-academy.jp/',
    },
    about: {
      '@type': 'Person',
      '@id': 'https://butlerbell.jp/#person',
      name: lang === 'en' ? 'Butler Bell' : '執事ベル',
      jobTitle: lang === 'en'
        ? 'President, Japan Butler Academy / Third Todori (head) of The All Japan Butler Association / Representative Director, The All Japan Butler Association (an incorporated association)'
        : '日本執事アカデミー 学長 / 全日本執事協会 三代目頭取（一般社団法人全日本執事協会 代表理事）',
      url: BUTLER_BELL_URL,
    },
  };

  return (
    <>
      <Header />
      <main>
        <Seo route="/president" jsonLd={[breadcrumbStructuredData, webPageStructuredData]} />

        {/* Hero */}
        <section className="hero-billboard relative w-full h-[60vh] min-h-[460px] md:h-[82vh] md:min-h-[640px] overflow-hidden">
          <img
            src="/images/president/president-portrait.png"
            alt={t('president.hero.imgAlt')}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 hero-overlay"></div>
          <div className="absolute inset-0 flex items-center">
            <div className="relative w-full px-8">
              <div className="max-w-[800px] mx-auto text-center">
                <h1 className="font-heading text-2xl md:text-4xl text-foreground-50 tracking-[0.08em] mb-4">
                  {t('president.hero.title')}
                </h1>
                {String(t('president.hero.titleEn')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="section-title-en text-accent-400 text-[15px] md:text-[17px] tracking-[0.12em] mb-6">{__s}</p>))}
                {String(t('president.hero.lead')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[15px] md:text-[16px] text-foreground-200/80 tracking-[0.05em] leading-relaxed max-w-[620px] mx-auto">{__s}</p>))}
              </div>
            </div>
          </div>
        </section>
      <Breadcrumb items={[{ label: 'アカデミーについて', labelEn: 'About the Academy', to: '/about' }, { label: '学長挨拶', labelEn: 'Message from the President' }]} />

        {/* Greeting letter */}
        <FadeInSection>
          <section className="relative py-20 md:py-28 bg-background-50">
            <div className="max-w-[820px] mx-auto px-8">
              <div className="text-center mb-12">
                {String(t('president.greeting.label')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-accent-600 text-[13px] tracking-[0.2em] mb-5 font-label">{__s}</p>))}
                <h2 className="font-heading text-xl md:text-2xl text-foreground-900 tracking-[0.06em] mb-2">
                  {t('president.greeting.title')}
                </h2>
                {String(t('president.greeting.subtitleEn')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="section-title-en text-accent-500 text-[15px] tracking-[0.12em] mb-4">{__s}</p>))}
                <div className="gold-line mx-auto"></div>
              </div>
              <div className="space-y-6 text-[15px] md:text-[16px] text-foreground-600 tracking-[0.03em] leading-loose text-left">
                {paragraphs.map((n) => (
                  <p key={n}>{t(`president.greeting.para${n}`)}</p>
                ))}
              </div>
              <div className="mt-14 text-right pr-2">
                {String(t('president.signature.name')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="font-heading text-[18px] text-foreground-900 tracking-[0.08em] mb-1">{__s}</p>))}
                {String(t('president.signature.title')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[13px] text-foreground-500 tracking-[0.04em]">{__s}</p>))}
              </div>
            </div>
          </section>
        </FadeInSection>

        <FadeInSection>
          <section className="relative py-20 md:py-24 bg-background-50">
            <div className="max-w-[820px] mx-auto px-8">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-900 mb-6">{t('book.profile.h2')}</h2>
              <div className="gold-line mb-8" />
              <p className="!text-[16px] !leading-[1.9] text-foreground-600 mb-8">{t('book.profile.p')}</p>
              <Link to={href('/book/butler-manners-and-culture')} className="text-accent-600 underline underline-offset-4">{t('book.profile.link')}</Link>
            </div>
          </section>
        </FadeInSection>

        {/* Links to Butler Bell */}
        <FadeInSection>
          <section className="relative py-20 md:py-24 bg-background-100/50">
            <div className="max-w-[720px] mx-auto px-8 text-center">
              {String(t('president.links.label')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-accent-600 text-[13px] tracking-[0.2em] mb-5 font-label">{__s}</p>))}
              <h2 className="font-heading text-xl md:text-2xl text-foreground-900 tracking-[0.06em] mb-2">
                {t('president.links.title')}
              </h2>
              {String(t('president.links.subtitleEn')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="section-title-en text-accent-500 text-[15px] tracking-[0.12em] mb-6">{__s}</p>))}
              <div className="gold-line mx-auto mb-8"></div>
              {String(t('president.links.body')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-[15px] text-foreground-600 tracking-[0.03em] leading-relaxed mb-8">{__s}</p>))}
              <div className="flex flex-wrap items-center justify-center gap-4">
                <Link
                  to={href('/related/butler-bell')}
                  className="inline-flex items-center gap-2 border border-accent-500/50 hover:border-accent-400 text-accent-600 text-[15px] tracking-[0.05em] px-8 py-3 transition-colors"
                >
                  {t('president.links.internalCta')}
                </Link>
                <a
                  href={BUTLER_BELL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-accent-500 hover:bg-accent-400 text-primary-900 text-[15px] tracking-[0.05em] px-8 py-3 transition-colors"
                >
                  <span>{t('president.links.externalCta')}</span>
                  <i className="ri-external-link-line text-sm"></i>
                </a>
              </div>
              <p className="mt-4 text-[13px] text-foreground-400 tracking-[0.04em]">
                {t('president.links.externalUrl')} — {t('related.common.externalNote')}
              </p>
            </div>
          </section>
        </FadeInSection>

        {/* Back to about / association */}
        <div className="bg-background-50 py-10 text-center flex flex-wrap items-center justify-center gap-4">
          <Link
            to={href('/about')}
            className="text-[15px] text-accent-600 hover:text-accent-500 tracking-[0.05em] transition-colors"
          >
            {t('president.backToAbout')}
          </Link>
          <span className="text-foreground-300">|</span>
          <Link
            to={href('/association')}
            className="text-[15px] text-accent-600 hover:text-accent-500 tracking-[0.05em] transition-colors"
          >
            {t('president.links.associationCta')}
          </Link>
        </div>

        {/* Last updated */}
        <div className="bg-background-100 py-8">
          {String(t('president.lastUpdated')).split(/\n{2,}/).map((__s, __i) => (<p key={__i} className="text-center text-[13px] text-foreground-400 tracking-[0.05em]">{__s}</p>))}
        </div>

        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-8 right-8 w-11 h-11 rounded-full border border-accent-500/40 text-accent-500 hover:bg-accent-500/10 hover:border-accent-400 transition-all duration-300 flex items-center justify-center cursor-pointer z-40"
          aria-label={t('related.common.backToTop')}
        >
          <i className="ri-arrow-up-line"></i>
        </button>
      </main>
      <Footer />
    </>
  );
}
